// Klien API + pengurusan sesi (Firebase Auth atau log masuk dev untuk demo tempatan).
// Kredential Firebase Admin TIDAK pernah berada di sini — frontend hanya menghantar ID token.

import { t, getLocale, setLocale, hasExplicitChoice } from './i18n.js';

export const API_BASE = window.STAYD_API_BASE || '';

const state = { config: null, me: null, firebase: null, devToken: null, listeners: new Set() };
export const session = state;

function store(key, val) {
  try {
    if (val === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, val);
  } catch { /* storan disekat — sesi kekal dalam memori */ }
}
function load(key) {
  try { return sessionStorage.getItem(key); } catch { return null; }
}

export class ApiError extends Error {
  constructor(status, code, message, details) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

async function token() {
  if (state.devToken) return state.devToken;
  if (state.firebase?.auth?.currentUser) return state.firebase.auth.currentUser.getIdToken();
  return null;
}

export async function api(method, path, body, { headers = {}, form = false } = {}) {
  const h = { 'X-Locale': getLocale(), ...headers };
  const tok = await token();
  if (tok) h.Authorization = `Bearer ${tok}`;
  let payload;
  if (form) payload = body;
  else if (body !== undefined) {
    h['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  }
  let res;
  try {
    res = await fetch(API_BASE + path, { method, headers: h, body: payload });
  } catch {
    throw new ApiError(0, 'NETWORK', t('err.network'));
  }
  const ct = res.headers.get('content-type') || '';
  if (!ct.includes('application/json')) {
    if (!res.ok) throw new ApiError(res.status, 'HTTP_' + res.status, t('err.request_failed'));
    return res;
  }
  const data = await res.json();
  if (!data.success) throw new ApiError(res.status, data.error?.code, data.error?.message || t('err.request_failed'), data.error?.details);
  return data.data;
}
export const get = (p) => api('GET', p);
export const post = (p, b, o) => api('POST', p, b, o);
export const put = (p, b) => api('PUT', p, b);
export const patch = (p, b) => api('PATCH', p, b);
export const del = (p) => api('DELETE', p);

export async function loadConfig() {
  state.config = await get('/api/config');
  return state.config;
}

export function onSession(fn) {
  state.listeners.add(fn);
  return () => state.listeners.delete(fn);
}
function emit() {
  for (const fn of state.listeners) fn(state.me);
}

export async function refreshMe() {
  try {
    const tok = await token();
    if (!tok) { state.me = null; return null; }
    state.me = await get('/api/me');
    state.me.uid = state.me.id;
    // Bahasa profil digunakan jika pengguna belum memilih bahasa pada peranti ini.
    if (state.me.locale && !hasExplicitChoice()) setLocale(state.me.locale, { persist: false });
  } catch (e) {
    if (e.status === 401 || e.status === 404) state.me = null;
    else throw e;
  }
  emit();
  return state.me;
}

// ---------- Firebase Auth (SDK web rasmi dari gstatic) ----------
const FB_VER = '10.12.2';
export async function initAuth() {
  const cfg = state.config;
  state.devToken = cfg.dev_auth ? load('stayd_dev_token') : null;
  if (cfg.firebase) {
    const [{ initializeApp }, authMod] = await Promise.all([
      import(`https://www.gstatic.com/firebasejs/${FB_VER}/firebase-app.js`),
      import(`https://www.gstatic.com/firebasejs/${FB_VER}/firebase-auth.js`),
    ]);
    const app = initializeApp(cfg.firebase);
    const auth = authMod.getAuth(app);
    state.firebase = { auth, mod: authMod };
    await new Promise((resolve) => {
      const off = authMod.onAuthStateChanged(auth, () => { off(); resolve(); });
    });
    authMod.onAuthStateChanged(auth, () => refreshMe());
  }
  await refreshMe();
}

export async function emailSignIn(email, password) {
  const { auth, mod } = state.firebase;
  await mod.signInWithEmailAndPassword(auth, email, password);
  return afterFirebaseSignIn();
}
export async function emailSignUp(email, password, name, phone) {
  const { auth, mod } = state.firebase;
  await mod.createUserWithEmailAndPassword(auth, email, password);
  const { turnstileToken } = await import('./turnstile.js');
  await post('/api/auth/register', { name, phone: phone || undefined, locale: getLocale(), turnstile_token: await turnstileToken() });
  return refreshMe();
}
export async function googleSignIn() {
  const { auth, mod } = state.firebase;
  const cred = await mod.signInWithPopup(auth, new mod.GoogleAuthProvider());
  return afterFirebaseSignIn(cred.user.displayName);
}
export async function resetPassword(email) {
  const { auth, mod } = state.firebase;
  await mod.sendPasswordResetEmail(auth, email);
}
async function afterFirebaseSignIn(displayName) {
  const r = await post('/api/auth/login');
  if (!r.registered) {
    const { turnstileToken } = await import('./turnstile.js');
    await post('/api/auth/register', { name: displayName || t('common.user'), locale: getLocale(), turnstile_token: await turnstileToken() });
  }
  return refreshMe();
}

export async function devLogin(userId) {
  const r = await post('/api/dev/login', { user_id: userId });
  state.devToken = r.token;
  store('stayd_dev_token', r.token);
  await post('/api/auth/login');
  return refreshMe();
}

export async function signOut() {
  state.devToken = null;
  store('stayd_dev_token', null);
  if (state.firebase) await state.firebase.mod.signOut(state.firebase.auth);
  state.me = null;
  emit();
}

export const hasRole = (...roles) => !!state.me && roles.some((r) => state.me.roles.includes(r));
export const isStaff = () => hasRole('SUPER_ADMIN', 'ADMIN', 'FINANCE', 'SUPPORT');

// Tukar bahasa: simpan pada peranti dan (jika log masuk) pada profil.
export async function changeLocale(code) {
  const changed = setLocale(code);
  if (state.me) {
    try { await put('/api/me', { locale: code }); state.me.locale = code; } catch { /* kekal tempatan */ }
  }
  return changed;
}
